export interface ConsentChoices {
  statistics: boolean;
  marketing: boolean;
}

const GA_ID = process.env.GATSBY_GA_MEASUREMENT_ID || "G-44T32BW2XG";
const ADS_ID = process.env.GATSBY_GOOGLE_ADS_ID || "";
const LINKEDIN_ID = process.env.GATSBY_LINKEDIN_PARTNER_ID || "";
const HUBSPOT_ID = process.env.GATSBY_HUBSPOT_PORTAL_ID || "";

export const marketingVendors = [
  ADS_ID && "google-ads",
  LINKEDIN_ID && "linkedin",
  HUBSPOT_ID && "hubspot",
].filter(Boolean) as string[];

// Changing the vendor list invalidates stored consent so visitors are asked again.
export const CONSENT_VERSION = ["v1", ...marketingVendors].join(":");

const enabled = process.env.NODE_ENV === "production";
const active: ConsentChoices = { statistics: false, marketing: false };

type W = Window & {
  dataLayer?: unknown[];
  gtag?: (...args: unknown[]) => void;
  _hsq?: unknown[][];
  _linkedin_partner_id?: string;
  _linkedin_data_partner_ids?: string[];
};

function loadScript(id: string, src: string) {
  if (document.getElementById(id)) return;
  const s = document.createElement("script");
  s.id = id;
  s.async = true;
  s.src = src;
  document.head.appendChild(s);
}

function ensureGtag(w: W) {
  if (w.gtag) return;
  w.dataLayer = w.dataLayer || [];
  w.gtag = function gtag() {
    // gtag.js requires the arguments object, not an array
    // eslint-disable-next-line prefer-rest-params
    w.dataLayer!.push(arguments);
  };
  w.gtag("consent", "default", {
    analytics_storage: "denied",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
  });
  w.gtag("js", new Date());
}

export function applyConsent(choices: ConsentChoices) {
  if (!enabled || typeof window === "undefined") return;
  const w = window as W;
  const useGoogle = choices.statistics || (choices.marketing && !!ADS_ID);

  if (useGoogle) {
    ensureGtag(w);
    const ads = choices.marketing ? "granted" : "denied";
    w.gtag!("consent", "update", {
      analytics_storage: choices.statistics ? "granted" : "denied",
      ad_storage: ads,
      ad_user_data: ads,
      ad_personalization: ads,
    });
    if (choices.statistics && !active.statistics) {
      w.gtag!("config", GA_ID, { anonymize_ip: true, send_page_view: false });
      w.gtag!("event", "page_view", { page_location: window.location.href });
    }
    if (choices.marketing && ADS_ID && !active.marketing) {
      w.gtag!("config", ADS_ID);
    }
    const tagId = choices.statistics ? GA_ID : ADS_ID;
    loadScript("ids-gtag", `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(tagId)}`);
  }

  if (choices.marketing && LINKEDIN_ID) {
    w._linkedin_partner_id = LINKEDIN_ID;
    w._linkedin_data_partner_ids = w._linkedin_data_partner_ids || [];
    if (!w._linkedin_data_partner_ids.includes(LINKEDIN_ID)) {
      w._linkedin_data_partner_ids.push(LINKEDIN_ID);
    }
    loadScript("ids-linkedin", "https://snap.licdn.com/li.lms-analytics/insight.min.js");
  }

  if (choices.marketing && HUBSPOT_ID) {
    loadScript("hs-script-loader", `https://js.hs-scripts.com/${encodeURIComponent(HUBSPOT_ID)}.js`);
  }

  active.statistics = choices.statistics;
  active.marketing = choices.marketing;
}

const TRACKING_COOKIE = /^(_ga|_gid|_gat|_gcl|li_|lidc|bcookie|UserMatchHistory|AnalyticsSyncHistory|__hs|hubspotutk)/;

export function clearTrackingCookies() {
  const host = window.location.hostname;
  const parts = host.split(".");
  const domains = ["", host, `.${host}`];
  if (parts.length > 2) domains.push(`.${parts.slice(-2).join(".")}`);
  document.cookie.split(";").forEach((c) => {
    const name = c.split("=")[0].trim();
    if (!TRACKING_COOKIE.test(name)) return;
    domains.forEach((d) => {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${d ? `; domain=${d}` : ""}`;
    });
  });
}

export function trackPageView() {
  if (!enabled) return;
  const w = window as W;
  if (active.statistics && w.gtag) {
    w.gtag("event", "page_view", { page_location: window.location.href });
  }
  if (active.marketing && HUBSPOT_ID) {
    w._hsq = w._hsq || [];
    w._hsq.push(["setPath", window.location.pathname + window.location.search]);
    w._hsq.push(["trackPageView"]);
  }
}
